import { Controller } from '@hotwired/stimulus'

// The card is the trigger for its own context menu: hovering builds and
// fetches the menu frame, clicking pops the dropdown it loads. The frame is
// created on demand so a grid of hundreds of cards carries none at rest.
export default class extends Controller {
  static values = { url: String, frameId: String }

  load() {
    // A frame removed to invalidate its cache reloads from scratch.
    if (this.frame?.isConnected) {
      return
    }

    // Parked invisibly over the whole card so the popup positions against it.
    // No overflow-hidden: a clipping ancestor of the reference would become
    // the popup's collision boundary and drag it back over the anchor. Inert
    // because the frame carries the menu's own trigger button, and an
    // invisible tab stop after every card is no better than no tab stop at
    // all; a click sent from script still reaches the button through it.
    const anchor = document.createElement('div')
    anchor.className = 'absolute inset-0 opacity-0 pointer-events-none'
    anchor.inert = true
    this.frame = document.createElement('turbo-frame')
    this.frame.className = 'block size-full'
    this.frame.setAttribute('id', this.frameIdValue)
    this.frame.setAttribute('src', this.urlValue)
    anchor.appendChild(this.frame)
    this.element.appendChild(anchor)
  }

  open(event) {
    // openMenu clicks the trigger inside the frame, and that click bubbles
    // back out through the card, which is this action's element. Left alone
    // the card would answer its own synthetic click by opening again.
    if (this.frame?.contains(event?.target)) {
      return
    }

    event?.preventDefault()
    this.load()

    if (this.loaded) {
      this.openMenu()
      return
    }

    if (!this.opening) {
      this.opening = true
      this.frame.addEventListener('turbo:frame-load', () => this.openMenu(), { once: true })
    }
  }

  openMenu() {
    this.frame.querySelector('[data-tippy-target="origin"], [data-dropdown-target="button"]')?.click()
  }

  get loaded() {
    return this.frame?.hasAttribute('complete')
  }
}
